import { Box, Typography } from "@mui/material";
import { ArcElement, Chart, Legend, Tooltip } from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { useEffect, useMemo, useState } from "react";
import { Doughnut } from "react-chartjs-2";

import { ISummary } from "./types";

Chart.register(ArcElement, Tooltip, Legend, ChartDataLabels);

interface IProps {
  beneficiaries: ISummary[];
}

interface IGenderData {
  label: string;
  count: number;
}

export function GraphGender({ beneficiaries }: IProps) {
  const [chartData, setChartData] = useState<any>({
    labels: [],
    datasets: [],
  });

  // Processa os dados por gênero
  const genderData: IGenderData[] = useMemo(() => {
    const genderMap = new Map<string, number>();

    beneficiaries.forEach((beneficiary) => {
      let genderLabel = "Não identificado";
      
      if (beneficiary.gender) {
        genderLabel = beneficiary.gender;
      }

      const currentCount = genderMap.get(genderLabel) || 0;
      genderMap.set(genderLabel, currentCount + 1);
    });

    return Array.from(genderMap.entries()).map(([label, count]) => ({
      label,
      count,
    }));
  }, [beneficiaries]);

  // Calcula o total para porcentagens
  const totalCount = useMemo(() => {
    return genderData.reduce((sum, item) => sum + item.count, 0);
  }, [genderData]);

  useEffect(() => {
    const labels = genderData.map((item) => item.label);
    const data = genderData.map((item) => item.count);

    // Define cores baseadas no número de gêneros
    const colors = ["#3024E1", "#F06292", "#d2d2d2"]; // Azul, Rosa, Laranja
    const backgroundColor = colors.slice(0, genderData.length);

    setChartData({
      labels: labels,
      datasets: [
        {
          label: "Pessoas",
          data: data,
          backgroundColor: backgroundColor,
          borderWidth: 1,
          borderRadius: 10,
        },
      ],
    });
  }, [genderData]);

  const options: any = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top" as const,
        labels: {
          boxWidth: 10,
          borderRadius: 10,
          color: "#1E1B39",
          font: {
            weight: "600",
            size: 16,
            lineHeight: 18,
          },
        },
      },
      datalabels: {
        formatter: (value: number, ctx: any) => {
          const percentage = Math.round((value / totalCount) * 100);
          return percentage + "%";
        },
        color: "#fff",
        font: {
          weight: "bold",
          size: 14,
        },
      },
    },
    cutout: "70%",
    animation: {
      duration: 0,
    },
    onHover: undefined,
    onResize: undefined,
  };

  return (
    <Box
      sx={{
        width: "100%",
        height: "500px",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
          justifyContent: "center",
          paddingTop: "0.25rem",
          paddingBottom: "1rem",
          flexShrink: 0,
        }}
      >
        <span
          style={{
            fontFamily: "Sora",
            fontSize: "1.25rem",
            fontWeight: 600,
            lineHeight: "2rem",
            color: "rgba(15, 15, 30, 0.6)",
          }}
        >
          Divisão Gênero
        </span>
      </Box>
      <Box
        sx={{
          padding: "1rem",
          flexGrow: 1,
          minHeight: 0,
          position: "relative",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Box sx={{ width: "80%", height: "100%", position: "relative" }}>
          <Doughnut data={chartData} options={options} />
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              textAlign: "center",
            }}
          >
            <Typography variant="h6" sx={{ color: "#1E1B39", fontWeight: "bold" }}>
              {totalCount}
            </Typography>
            <Typography variant="body2" sx={{ color: "#1E1B39" }}>
              Funcionários
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
